/**
 * A schedule as `/teams/{id}/schedules/V2` returns it, flattened into
 * `useGetScheduleFiltering`'s `filters` buckets.
 */
export interface ScheduleFilterEntry {
  id?: string
  name?: string
  type?: string
  /**
   * The team's roster for THIS schedule. Populated on the level the team is
   * actually registered in — typically the Division — and null on the parent
   * Season, which the team joins through its division rather than directly.
   */
  team_roster_id?: string | null
  parent?: { id?: string } | null
}

export interface ScheduleFilterBucket {
  name?: string
  schedules?: ScheduleFilterEntry[]
}

/**
 * The roster id for the selected season/division, read from data already in
 * memory.
 *
 * `/teams/{id}/schedules/V2` now returns `team_roster_id` on each schedule, so
 * the roster is known without a separate `/teams/{id}/rosters` request.
 *
 * The lookup deliberately checks the DIVISION first. `team_roster_id` sits on
 * the level the team registered in — usually the division — and is null on the
 * parent season, so resolving by season id alone would find nothing. When no
 * division is selected, the closest DESCENDANT of the chosen schedule that
 * carries a roster identifies the same one. That descent is transitive:
 * competitions are not always Season > Division, and a Season > Conference >
 * Division hierarchy hides the division behind a roster-less conference.
 *
 * Returns undefined when nothing matches (All Seasons, or a team not in the
 * selected competition), which callers treat as "no roster scope".
 */
export const resolveRosterIdFromFilters = (
  filters: ScheduleFilterBucket[] | null | undefined,
  scheduleId?: string | null,
  divisionId?: string | null
): string | undefined => {
  if (!filters?.length) {
    return undefined
  }

  const all = filters.flatMap((bucket) =>
    Array.isArray(bucket?.schedules) ? bucket.schedules : []
  )

  const rosterIdOf = (entry: ScheduleFilterEntry | undefined) =>
    entry?.team_roster_id || undefined

  // 1. An explicitly selected division is the most specific answer.
  if (divisionId) {
    const byDivision = rosterIdOf(all.find((entry) => entry?.id === divisionId))
    if (byDivision) {
      return byDivision
    }
  }

  if (!scheduleId) {
    return undefined
  }

  // 2. The season itself, for competitions where the team registers at that
  //    level and the id therefore carries the roster.
  const bySeason = rosterIdOf(all.find((entry) => entry?.id === scheduleId))
  if (bySeason) {
    return bySeason
  }

  // 3. Otherwise a DESCENDANT of that schedule — the common shape, where the
  //    roster hangs off the division and the selected level's own entry is
  //    null.
  //
  //    Walked transitively rather than one level down: competitions are not
  //    always Season > Division. A Season > Conference > Division hierarchy
  //    puts the CONFERENCE in `division.parent`, so the season is only
  //    reachable by following the chain (`useGetScheduleFiltering` flattens
  //    each level into its own bucket, keeping a single-level `parent` on
  //    each). Checking one level found the roster-less conference and gave up.
  //
  //    Shallowest-first, so the closest descendant wins when more than one
  //    level carries a roster.
  const byId = new Map<string, ScheduleFilterEntry>()
  for (const entry of all) {
    if (entry?.id && !byId.has(entry.id)) {
      byId.set(entry.id, entry)
    }
  }

  /** Depth from `scheduleId` down to `entry`, or undefined if unrelated. */
  const depthBelowSelection = (
    entry: ScheduleFilterEntry
  ): number | undefined => {
    // Guards against a cyclic `parent` chain in malformed data, which would
    // otherwise spin forever.
    const seen = new Set<string>()
    let current = entry
    let depth = 0

    while (current?.parent?.id) {
      const parentId = current.parent.id
      depth += 1

      if (parentId === scheduleId) {
        return depth
      }
      if (seen.has(parentId)) {
        return undefined
      }
      seen.add(parentId)

      const parent = byId.get(parentId)
      if (!parent) {
        return undefined
      }
      current = parent
    }

    return undefined
  }

  let closest: { depth: number; rosterId: string } | undefined
  for (const entry of all) {
    const rosterId = rosterIdOf(entry)
    if (!rosterId) {
      continue
    }

    const depth = depthBelowSelection(entry)
    if (depth !== undefined && (!closest || depth < closest.depth)) {
      closest = { depth, rosterId }
    }
  }

  return closest?.rosterId
}
